// ============================================================
//  src/pages/settings/WebhookSettingsPage.js
//
//  Shows the user their personal webhook URL and verify token
//  to paste into their Meta App → WhatsApp → Configuration.
//  One token per account — works for all bots on the account.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getWebhookSettings, regenerateWebhookToken } from '../../api/client';

// ── tiny helpers ─────────────────────────────────────────────
function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard!'),
      () => toast.error('Copy failed — please copy manually.'),
    );
  } else {
    // Fallback for older browsers
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity  = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    toast.success('Copied!');
  }
}

function CopyField({ label, value, mono = true }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 11, fontWeight: 800, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--bg-main)',
        border: '1.5px solid var(--border)',
        borderRadius: 10, padding: '11px 14px',
      }}>
        <span style={{
          flex: 1, fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit',
          color: 'var(--text-dark)', wordBreak: 'break-all',
          userSelect: 'all',
        }}>
          {value || '—'}
        </span>
        <button
          onClick={() => copyToClipboard(value)}
          style={{
            flexShrink: 0,
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Copy
        </button>
      </div>
    </div>
  );
}

function Step({ num, children }) {
  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 14, alignItems: 'flex-start' }}>
      <div style={{
        flexShrink: 0,
        width: 26, height: 26, borderRadius: '50%',
        background: 'rgba(0,44,237,0.10)',
        color: 'var(--primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 800,
      }}>
        {num}
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--text-dark)', paddingTop: 3, lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  );
}

export default function WebhookSettingsPage() {
  const [settings, setSettings]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getWebhookSettings();
      setSettings(res.data ?? null);
    } catch {
      toast.error('Could not load webhook settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRegenerate() {
    setRegenerating(true);
    setShowConfirm(false);
    try {
      const res = await regenerateWebhookToken();
      const newToken = res.data?.verify_token;
      if (newToken) {
        setSettings(prev => ({ ...prev, verify_token: newToken }));
        toast.success('Token regenerated! Update your Meta App webhook settings.');
      }
    } catch {
      toast.error('Failed to regenerate token.');
    } finally {
      setRegenerating(false);
    }
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 10, padding: 60, color: 'var(--text-muted)', fontSize: 14,
      }}>
        <span style={{
          display: 'inline-block', width: 18, height: 18,
          border: '2.5px solid var(--border)', borderTopColor: 'var(--primary)',
          borderRadius: '50%', animation: 'spin 0.7s linear infinite',
        }} />
        Loading…
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 24px', maxWidth: 680, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 22, fontWeight: 800, color: 'var(--text-dark)', margin: 0,
        }}>
          Webhook Settings
        </h1>
        <p style={{
          margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.5,
        }}>
          Paste these values into your Meta App &rarr; WhatsApp &rarr; Configuration.
          One set covers all your WhatsApp bots.
        </p>
      </div>

      {/* Values card */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1.5px solid var(--border)',
        borderRadius: 16,
        padding: '24px 24px 20px',
        marginBottom: 20,
      }}>
        <CopyField label="Callback URL" value={settings?.webhook_url} mono={false} />
        <CopyField label="Verify Token" value={settings?.verify_token} />

        {/* Regenerate */}
        {!showConfirm ? (
          <button
            onClick={() => setShowConfirm(true)}
            disabled={regenerating}
            style={{
              marginTop: 4,
              background: 'transparent',
              border: '1.5px solid var(--border)',
              borderRadius: 8,
              padding: '7px 16px',
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--danger)';
              e.currentTarget.style.color = 'var(--danger)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            ↺ Regenerate Token
          </button>
        ) : (
          <div style={{
            marginTop: 8,
            background: 'rgba(255,59,48,0.07)',
            border: '1.5px solid rgba(255,59,48,0.25)',
            borderRadius: 10,
            padding: '14px 16px',
          }}>
            <p style={{
              margin: '0 0 12px', fontSize: 13, color: 'var(--danger)', fontWeight: 600,
            }}>
              ⚠️ Regenerating will break all existing Meta webhook subscriptions until
              you update the Verify Token in your Meta App. Are you sure?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                style={{
                  background: 'var(--danger)', color: '#fff',
                  border: 'none', borderRadius: 7,
                  padding: '7px 18px', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {regenerating ? 'Regenerating…' : 'Yes, Regenerate'}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  background: 'transparent', color: 'var(--text-muted)',
                  border: '1.5px solid var(--border)', borderRadius: 7,
                  padding: '7px 18px', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Setup guide */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1.5px solid var(--border)',
        borderRadius: 16,
        padding: '22px 24px',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 800, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 16,
        }}>
          How to connect in Meta App
        </div>

        <Step num={1}>
          Go to <strong>Meta for Developers</strong> &rarr; your App &rarr; <strong>WhatsApp</strong> &rarr; <strong>Configuration</strong>.
        </Step>
        <Step num={2}>
          Under <strong>Webhook</strong>, click <strong>Edit</strong> and paste the <strong>Callback URL</strong> above.
        </Step>
        <Step num={3}>
          Paste the <strong>Verify Token</strong> above into the Verify Token field. Click <strong>Verify and Save</strong>.
        </Step>
        <Step num={4}>
          Subscribe to the <strong>messages</strong> webhook field. You're done — this token works for all your bots.
        </Step>

        <div style={{
          marginTop: 16,
          padding: '12px 14px',
          background: 'rgba(0,44,237,0.05)',
          border: '1px solid rgba(0,44,237,0.15)',
          borderRadius: 9,
          fontSize: 12.5,
          color: 'var(--text-muted)',
          lineHeight: 1.55,
        }}>
          💡 <strong>One token, all bots.</strong> The same Callback URL and Verify Token work
          for every WhatsApp bot in your account. Meta uses the phone number ID inside each
          message payload to route to the correct bot automatically.
        </div>
      </div>

    </div>
  );
}